import { motion } from 'framer-motion';
import Hyperspeed from './Hyperspeed';
import { hyperspeedPresets } from './HyperSpeedPresets';

export const LoadingScreen = ({ variant = 'initial' }: { variant?: 'initial' | 'navigation' }) => {
    const kiteStyles = `
        .kite-loader-container {
            display: flex;
            align-items: center;
            gap: 4px;
            margin: 1em;
            justify-content: center;
        }
        .dash {
            animation: dashArray 2s ease-in-out infinite, dashOffset 2s linear infinite;
        }
        @keyframes dashArray {
            0% { stroke-dasharray: 0 1 359 0; }
            50% { stroke-dasharray: 0 359 1 0; }
            100% { stroke-dasharray: 359 1 0 0; }
        }
        @keyframes dashOffset {
            0% { stroke-dashoffset: 365; }
            100% { stroke-dashoffset: 5; }
        }
    `;

    return (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black overflow-hidden">
            <style>{kiteStyles}</style>
            {/* 3D Hyperspeed Background - Disabled in preview/recorder mode to avoid WebGL crashes */}
            <div className="absolute inset-0 z-0">
                {!window.location.search.includes('preview=true') && (
                    <Hyperspeed effectOptions={hyperspeedPresets.cyberpunk} />
                )}
            </div>

            {/* Overlay darkening for better logo visibility */}
            <div className="absolute inset-0 bg-black/40 z-[1]" />

            <div className="relative z-10 flex flex-col items-center gap-6">
                {/* Branding Section */}
                <div className="flex flex-col items-center gap-4">
                    {/* Animated Logo Container */}
                    <div className="relative w-28 h-28">
                        <motion.div
                            className="absolute inset-0 flex items-center justify-center"
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{
                                scale: [1, 1.05, 1],
                                opacity: 1
                            }}
                            transition={{
                                scale: { duration: 3, repeat: Infinity, ease: "easeInOut" },
                                opacity: { duration: 0.8 }
                            }}
                        >
                            <img
                                src="/logo.png"
                                alt="App Kite Logo"
                                className="w-24 h-24 object-contain relative z-10 drop-shadow-[0_0_20px_rgba(59,130,246,0.5)]"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    e.currentTarget.parentElement!.innerHTML = '<span class="text-4xl text-blue-500 font-black">K</span>';
                                }}
                            />

                            {/* Core Glow */}
                            <motion.div
                                className="absolute inset-2 rounded-full bg-blue-500/10 blur-2xl"
                                animate={{
                                    scale: [1, 1.4, 1],
                                    opacity: [0.3, 0.6, 0.3]
                                }}
                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            />
                        </motion.div>
                    </div>

                    <div className="kite-loader-container -mt-4">
                        {/* Gradient Definitions */}
                        <svg height="0" width="0" viewBox="0 0 64 64" className="absolute">
                            <defs>
                                <linearGradient id="grad-k" gradientUnits="userSpaceOnUse" y2="2" x2="0" y1="62" x1="0">
                                    <stop stopColor="#973BED"></stop>
                                    <stop stopColor="#007CFF" offset="1"></stop>
                                </linearGradient>
                                <linearGradient id="grad-i" gradientUnits="userSpaceOnUse" y2="2" x2="0" y1="62" x1="0">
                                    <stop stopColor="#00E0ED"></stop>
                                    <stop stopColor="#00DA72" offset="1"></stop>
                                </linearGradient>
                                <linearGradient id="grad-t" gradientUnits="userSpaceOnUse" y2="2" x2="0" y1="62" x1="0">
                                    <stop stopColor="#FFC800"></stop>
                                    <stop stopColor="#F0F" offset="1"></stop>
                                </linearGradient>
                                <linearGradient id="grad-e" gradientUnits="userSpaceOnUse" y2="2" x2="0" y1="62" x1="0">
                                    <stop stopColor="#FF4D4D"></stop>
                                    <stop stopColor="#F9CB28" offset="1"></stop>
                                </linearGradient>
                            </defs>
                        </svg>

                        {/* Letter K */}
                        <svg viewBox="0 0 48 64" height="64" width="48" className="inline-block">
                            <path className="dash" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" 
                                  stroke="url(#grad-k)" fill="none" pathLength="360"
                                  d="M 8,4 V 60 M 44,4 L 8,32 L 44,60" />
                        </svg>

                        {/* Letter I */}
                        <svg viewBox="0 0 20 64" height="64" width="20" className="inline-block">
                            <path className="dash" strokeWidth="8" strokeLinecap="round" 
                                  stroke="url(#grad-i)" fill="none" pathLength="360"
                                  d="M 10,8 A 0.5,0.5 0 1 1 9.9,8 Z" />
                            <path className="dash" strokeWidth="8" strokeLinecap="round" 
                                  stroke="url(#grad-i)" fill="none" pathLength="360"
                                  d="M 10,28 V 60" />
                        </svg>

                        {/* Letter T */}
                        <svg viewBox="0 0 48 64" height="64" width="48" className="inline-block">
                            <path className="dash" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" 
                                  stroke="url(#grad-t)" fill="none" pathLength="360"
                                  d="M 24,4 V 60 M 4,4 H 44" />
                        </svg>

                        {/* Letter E */}
                        <svg viewBox="0 0 44 64" height="64" width="44" className="inline-block">
                            <path className="dash" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" 
                                  stroke="url(#grad-e)" fill="none" pathLength="360"
                                  d="M 40,4 H 8 V 60 H 40 M 8,32 H 32" />
                        </svg>
                    </div>
                </div>

                {/* Description Section - Only for Initial */}
                {variant === 'initial' && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.8 }}
                        className="flex flex-col items-center gap-4"
                    >
                        {/* Animated Divider */}
                        <motion.div
                            className="w-12 h-[1px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"
                            animate={{ width: [48, 80, 48] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        />

                        <div className="flex flex-col items-center gap-1">
                            <span className="text-xs font-bold text-blue-400 uppercase tracking-[0.4em] drop-shadow-[0_0_8px_rgba(96,165,250,0.5)]">
                                Telegram Usernames
                            </span>
                            <div className="flex items-center gap-3">
                                <span className="text-[9px] font-black text-white/40 uppercase tracking-[0.5em] flex items-center gap-2">
                                    <span>NFT</span>
                                    <span className="w-1 h-1 rounded-full bg-blue-500/30" />
                                    <span>Ownership</span>
                                </span>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Pulsing Status */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: variant === 'initial' ? 1.2 : 0.4 }}
                    className="mt-4 flex items-center gap-3 px-4 py-1.5 rounded-full border border-white/5 bg-white/5 backdrop-blur-md"
                >
                    <motion.div
                        className="w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_10px_#3B82F6]"
                        animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.2, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                    />
                    <span className="text-[10px] font-bold text-white/60 uppercase tracking-[0.2em]">
                        {variant === 'initial' ? 'Synchronizing Protocol' : 'Loading'}
                    </span>
                </motion.div>
            </div>
        </div>
    );
};
